import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import type { ImportProgress } from '../../../types/import';
import {
  formatFileFailedMessage,
  isPartialImport,
} from '../utils/importDerivedState';

type Props = {
  progress: ImportProgress;
};

type StepKey = 'upload' | 'analyze' | 'done';

function stepState(
  progress: ImportProgress,
): Record<StepKey, 'pending' | 'active' | 'done' | 'failed'> {
  if (progress.phase === 'failed') {
    return {
      upload: progress.uploadIndex > 0 || progress.uploadTotal > 0 ? 'done' : 'failed',
      analyze: 'failed',
      done: 'failed',
    };
  }
  if (progress.phase === 'complete') {
    if (isPartialImport(progress)) {
      return { upload: 'done', analyze: 'failed', done: 'done' };
    }
    return { upload: 'done', analyze: 'done', done: 'done' };
  }
  if (progress.phase === 'uploading') {
    return { upload: 'active', analyze: 'pending', done: 'pending' };
  }
  if (progress.phase === 'processing') {
    return { upload: 'done', analyze: 'active', done: 'pending' };
  }
  return { upload: 'pending', analyze: 'pending', done: 'pending' };
}

function StepDot({
  label,
  state,
}: {
  label: string;
  state: 'pending' | 'active' | 'done' | 'failed';
}) {
  return (
    <View style={styles.stepItem}>
      <View
        style={[
          styles.dot,
          state === 'active' && styles.dotActive,
          state === 'done' && styles.dotDone,
          state === 'failed' && styles.dotFailed,
        ]}
      >
        {state === 'active' ? (
          <ActivityIndicator color={colors.background} size="small" />
        ) : (
          <Text style={styles.dotText}>
            {state === 'done' ? '✓' : state === 'failed' ? '!' : ''}
          </Text>
        )}
      </View>
      <Text
        style={[
          styles.stepLabel,
          state === 'active' && styles.stepLabelActive,
          state === 'done' && styles.stepLabelDone,
          state === 'failed' && styles.stepLabelFailed,
        ]}
      >
        {label}
      </Text>
    </View>
  );
}

export function ImportProgressPanel({ progress }: Props) {
  const fileErrors = progress.fileErrors ?? [];
  if (progress.phase === 'idle' && !progress.error && fileErrors.length === 0) {
    return null;
  }

  const partial = isPartialImport(progress);
  const showSpinner = progress.phase === 'uploading' || progress.phase === 'processing';
  const steps = stepState(progress);
  const ratio =
    progress.phase === 'uploading' && progress.uploadTotal > 0
      ? progress.uploadIndex / progress.uploadTotal
      : progress.totalImages > 0
        ? Math.min(
            1,
            (progress.processedImages + (progress.phase === 'processing' ? 0.35 : 0)) /
              progress.totalImages,
          )
        : progress.phase === 'complete'
          ? 1
          : progress.phase === 'processing'
            ? 0.45
            : 0;
  const showWarn = progress.phase !== 'failed' && partial;
  const fillColor = progress.phase === 'failed'
    ? colors.danger
    : showWarn
      ? colors.warning
      : colors.accent;

  return (
    <View
      style={[
        styles.wrap,
        progress.phase === 'failed' && styles.wrapFailed,
        showWarn && styles.wrapWarn,
      ]}
      accessibilityLiveRegion="polite"
    >
      <View style={styles.steps}>
        <StepDot label="Upload" state={steps.upload} />
        <View style={styles.stepRail} />
        <StepDot label="Analyze" state={steps.analyze} />
        <View style={styles.stepRail} />
        <StepDot label="Done" state={steps.done} />
      </View>

      <Text
        style={[
          styles.status,
          progress.phase === 'failed' && styles.statusError,
          showWarn && styles.statusWarn,
          progress.phase === 'complete' && !partial && styles.statusOk,
        ]}
      >
        {progress.statusMessage || (progress.error ?? '')}
      </Text>

      {(showSpinner || progress.phase === 'complete') && (
        <View style={styles.track}>
          <View
            style={[
              styles.fill,
              { width: `${Math.round(Math.max(0.06, ratio) * 100)}%`, backgroundColor: fillColor },
            ]}
          />
        </View>
      )}

      {progress.phase === 'processing' ? (
        <Text style={styles.hint}>
          {progress.processedImages > 0 && progress.totalImages > 0
            ? `File ${Math.min(progress.processedImages, progress.totalImages)} of ${progress.totalImages} analyzed. PDFs can take ~30–60s each.`
            : 'Cloud upload done — AI extraction & categorize next. Statement PDFs can take ~30–60s.'}
        </Text>
      ) : null}

      {fileErrors.length > 0 ? (
        <View style={styles.fileErrors}>
          {fileErrors.map((entry) => (
            <Text
              key={`${entry.imageIndex}:${entry.s3Key}`}
              style={styles.fileError}
            >
              {formatFileFailedMessage(entry)}
            </Text>
          ))}
        </View>
      ) : null}

      {progress.transactionCount > 0 && progress.phase !== 'idle' ? (
        <Text style={styles.meta}>
          {progress.transactionCount} transaction{progress.transactionCount === 1 ? '' : 's'}
          {progress.duplicateCount > 0 ? ` · ${progress.duplicateCount} duplicate` : ''}
        </Text>
      ) : null}

      {progress.recentMerchants.length > 0 ? (
        <Text style={styles.merchants} numberOfLines={2}>
          {progress.recentMerchants.slice(0, 5).join(' · ')}
        </Text>
      ) : null}

      {progress.error && progress.phase === 'failed' ? (
        <Text style={styles.errorDetail}>{progress.error}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
  },
  wrapWarn: {
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
    borderColor: 'rgba(245, 158, 11, 0.28)',
  },
  wrapFailed: {
    backgroundColor: 'rgba(248, 113, 113, 0.08)',
    borderColor: 'rgba(248, 113, 113, 0.28)',
  },
  steps: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  stepItem: {
    alignItems: 'center',
    gap: 4,
    minWidth: 56,
  },
  stepRail: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.glassBorder,
    marginBottom: 16,
  },
  dot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotActive: {
    borderColor: colors.accent,
    backgroundColor: colors.accent,
  },
  dotDone: {
    borderColor: colors.accent,
    backgroundColor: 'rgba(61, 214, 164, 0.25)',
  },
  dotFailed: {
    borderColor: colors.danger,
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
  },
  dotText: {
    ...typography.label,
    color: colors.accent,
    fontSize: 11,
    fontWeight: '700',
  },
  stepLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  stepLabelActive: {
    color: colors.text,
  },
  stepLabelDone: {
    color: colors.accent,
  },
  stepLabelFailed: {
    color: colors.danger,
  },
  status: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  statusError: {
    color: colors.danger,
  },
  statusOk: {
    color: colors.accent,
  },
  statusWarn: {
    color: colors.warning,
  },
  track: {
    height: 6,
    borderRadius: radii.pill,
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: radii.pill,
    backgroundColor: colors.accent,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  meta: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  merchants: {
    ...typography.caption,
    color: colors.textMuted,
  },
  fileErrors: {
    gap: 4,
  },
  fileError: {
    ...typography.caption,
    color: colors.warning,
  },
  errorDetail: {
    ...typography.caption,
    color: colors.danger,
  },
});
